import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocuments, uploadDocument, deleteDocument, setTokenProvider } from '../api/client';
import { useAuth } from '../context/AuthContext';

const MODULES = ['warranty', 'project-management', 'cad-development', 'sales-pricing', 'owner-strategic', 'shared-core'];

interface Document {
  key: string;
  module: string;
  size: number;
  lastModified: string;
}

export function AdminPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [selectedModule, setSelectedModule] = useState('warranty');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    setTokenProvider(getToken);
    loadDocuments();
  }, [selectedModule]);

  async function loadDocuments() {
    setLoading(true);
    setError('');
    try {
      const data = await getDocuments(selectedModule);
      setDocuments(data.documents || []);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
      setError(err.message || 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadStatus('Uploading...');
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        await uploadDocument(file.name, selectedModule, base64);
        setUploadStatus('Upload complete! KB will sync in 2-5 minutes.');
        loadDocuments();
        setTimeout(() => setUploadStatus(''), 5000);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setUploadStatus(`Upload failed: ${err.message}`);
    }
  }

  async function handleDelete(key: string) {
    if (!confirm(`Delete ${key}?`)) return;
    try {
      await deleteDocument(key);
      loadDocuments();
    } catch (err: any) {
      alert(`Delete failed: ${err.message}`);
    }
  }

  return (
    <div style={{ background: '#f9fafb', padding: '24px' }}>
      <div style={{ maxWidth: '896px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">
            ← Back
          </button>
          <h3 className="font-medium text-gray-700 text-lg">Document Manager</h3>
        </div>

        {/* Module selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Module</label>
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            className="px-3 py-2 border rounded-md w-64"
          >
            {MODULES.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Upload */}
        <div className="mb-6 p-4 bg-white rounded-lg border">
          <h4 className="font-medium mb-2">Upload Document</h4>
          <input
            type="file"
            accept=".docx,.pdf,.xlsx,.md"
            onChange={handleUpload}
            className="text-sm"
          />
          {uploadStatus && <p className="mt-2 text-sm text-blue-600">{uploadStatus}</p>}
        </div>

        {/* Document list */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h4 className="font-medium">Documents in {selectedModule} ({documents.length})</h4>
          </div>
          {error ? (
            <div className="p-4 text-red-600 bg-red-50">
              <p className="font-medium">Error loading documents</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          ) : loading ? (
            <div className="p-4 text-gray-500">Loading...</div>
          ) : documents.length === 0 ? (
            <div className="p-4 text-gray-500">No documents in this module.</div>
          ) : (
            <ul className="divide-y">
              {documents.map((doc) => (
                <li key={doc.key} className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{doc.key.split('/').pop()}</p>
                    <p className="text-xs text-gray-500">
                      {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.lastModified).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(doc.key)}
                    className="text-red-500 text-sm hover:text-red-700"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
